import Button from '@jetbrains/ring-ui/components/button/button'
import classNames from 'classnames'
import type {MouseEvent} from 'react'
import * as React from 'react'
import {useCallback, useState} from 'react'

import {useAppSelector} from '../../../../../../../hooks/react-redux'
import {ShowFeedbackFormReasons} from '../Feedback.slice'

import FeedbackFormSection from './FeedbackForm.section'

import styles from './FeedbackForm.css'

const tags = {
  dissatisfied: [
    'Hard to set up',
    'Complex interface',
    'I have no knowledge of YAML',
    'Hard to optimise',
    'Navigation',
    'Not enough settings and information',
  ],
  stuck: [
    'Hard to set up a pipeline',
    'Complex interface',
    'Navigation',
    'Not enough settings and information',
  ],
  satisfied: [
    'Easy to set up the pipeline',
    'Friendly interface',
    'Navigation',
    'Visual editor',
    'YAML',
    'Easy to optimise this pipeline',
    'Enough settings and information',
  ],
}

const THRESHOLD_RATING = 4
function FeedbackFormTags({onTagsSelect}: {onTagsSelect: (items: Array<string>) => void}) {
  const rating = useAppSelector(state => state.pipelines.feedbackForm.userInput.rating)
  const reason = useAppSelector(state => state.pipelines.feedbackForm.visibility.reason)

  const ratingRef = React.useRef(rating)
  const [selectedTags, setSelectedTags] = useState<Array<string>>([])
  const select = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    const tag = event.currentTarget?.dataset?.key
    setSelectedTags(state => {
      if (!tag) {
        return state
      }

      return state.includes(tag) ? state.filter(t => t !== tag) : [...state, tag]
    })
  }, [])

  React.useEffect(() => {
    onTagsSelect(selectedTags)
  }, [onTagsSelect, selectedTags])

  React.useEffect(() => {
    if (ratingRef.current !== rating) {
      if (
        (ratingRef.current > THRESHOLD_RATING && rating <= THRESHOLD_RATING) ||
        (ratingRef.current <= THRESHOLD_RATING && rating > THRESHOLD_RATING)
      ) {
        setSelectedTags([])
      }
    }
  }, [rating])

  const content = React.useMemo(() => {
    if (reason === ShowFeedbackFormReasons.STUCK) {
      return {
        title: 'Struggling with your pipeline?',
        text: 'Let us know if you experience any difficulties, and we will get back to you in no time.',
        tags: tags.stuck,
      }
    }

    return {
      title:
        rating > THRESHOLD_RATING
          ? 'Which of the following did you like the most?'
          : 'Which of the following did you find most challenging and annoying?',
      text: '',
      tags: rating > THRESHOLD_RATING ? tags.satisfied : tags.dissatisfied,
    }
  }, [reason, rating])

  return (
    <FeedbackFormSection title={content.title} text={content.text}>
      <div className={styles.tags}>
        {content.tags.map(tag => (
          <Button
            className={classNames(styles.tag, selectedTags.includes(tag) ? styles.active : null)}
            key={tag}
            onClick={select}
            data-key={tag}
          >
            {tag}
          </Button>
        ))}
      </div>
    </FeedbackFormSection>
  )
}

export default FeedbackFormTags
